import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import { AlertTriangle, ChevronLeftIcon, Clock, IndianRupee, Play, Search, ShieldCheck, Users } from "lucide-react";
import { PRIMARY_BUTTON_CLASS } from "../../components/ui/buttonStyles";
import useApiMethods from "../../lib/useApiMethods";
import { PAYROLL_RUN_GET, PAYROLL_SHORT_DUTY_REVIEW } from "../../constants/api";
import { ContainerCard } from "../../components/ui/cards";
import { checkUserPermissionFunc } from "../../components/common/CommonFunctions";
import ToastMsg from "../../components/common/ToastMsg";
import { cn } from "../../utils/tailwindUtils";
import { Input } from "../../components/ui/input";
import { SelectField } from "../../components/ui/form-fields";

/**
 * Short-Duty Review — the pre-payroll gate.
 *
 * Reached from PayrollProcess (only when the company has short-duty deduction ON
 * and there are missing-punch days). For each day HR picks ONE penalty mode:
 *   • Deduct — a day fraction (0.5 = half day) → Loss of Pay
 *   • Fine   — a flat ₹ amount → a "Short-Duty Fine" debit
 * On Finalize the decisions are saved to attendance, then the payroll is created.
 */
const SHORT_DUTY_PAGE_SIZE = 25;
const ShortDutyReview = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { fetch, create, update } = useApiMethods();
    const { edit: editPermission } = checkUserPermissionFunc("payroll", "Payroll", "SubMenu");

    const payload = location.state?.payload;
    const prefetched = location.state?.review;

    const [loading, setLoading] = useState(!prefetched);
    const [running, setRunning] = useState(false);
    const [items, setItems] = useState([]);
    const [search, setSearch] = useState("");
    const [deptFilter, setDeptFilter] = useState("all");
    const [selected, setSelected] = useState(() => new Set());
    const [defaults, setDefaults] = useState({ fraction: 0.5, fine: 0 });
    const [bulkMode, setBulkMode] = useState("deduction");
    const [page, setPage] = useState(1);
    const [periodBlocked, setPeriodBlocked] = useState(false);

    // Without the payroll payload there's nothing to run — send them back.
    useEffect(() => {
        if (!payload) {
            ToastMsg("error", "Please start from Run Payroll");
            navigate("/payroll/payrollprocess", { replace: true });
        }
    }, [payload, navigate]);

    useEffect(() => {
        if (!payload) return;
        let cancelled = false;
        (async () => {
            const periodCheck = await create(
                PAYROLL_RUN_GET,
                { ...payload, validate_only: true },
                false,
            );
            if (cancelled) return;
            if (periodCheck?.status !== "success") {
                setPeriodBlocked(true);
                ToastMsg(
                    "error",
                    periodCheck?.message || "Payroll has already been processed for this period",
                );
                navigate("/payroll/payrollprocess", { replace: true });
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [payload]);

    useEffect(() => {
        if (!payload) return;
        const normalise = (data) => {
            setDefaults({
                fraction: Number(data?.config?.default_fraction ?? 0.5),
                fine: Number(data?.config?.default_fine ?? 0),
            });
            setItems((data?.items || []).map((it) => ({
                ...it,
                mode: it.mode === "fine" ? "fine" : "deduction",
                deduction: Number(it.deduction ?? 0),
                fine: Number(it.fine ?? 0),
            })));
        };
        if (prefetched) {
            normalise(prefetched);
            return;
        }
        const load = async () => {
            const res = await fetch(PAYROLL_SHORT_DUTY_REVIEW, {
                from_date: payload.from_date,
                to_date: payload.to_date,
                department: payload.department,
                employee_type: payload.employee_type,
            }, setLoading);
            if (res?.status === "success") normalise(res.data);
            else ToastMsg("error", res?.message || "Could not load short-duty days");
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ---- derived: department options + filtered rows ----
    const departments = useMemo(() => {
        const map = new Map();
        items.forEach((it) => { if (it.department_id) map.set(it.department_id, it.department_name || "-"); });
        return Array.from(map, ([id, name]) => ({ id, name }));
    }, [items]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return items.filter((it) => {
            const matchesSearch = !q
                || (it.employee_name || "").toLowerCase().includes(q)
                || (it.employee_code || "").toLowerCase().includes(q);
            const matchesDept = deptFilter === "all" || String(it.department_id) === String(deptFilter);
            return matchesSearch && matchesDept;
        });
    }, [items, search, deptFilter]);

    const filteredIds = useMemo(() => filtered.map((it) => it.attendance_id), [filtered]);
    const allFilteredSelected = filteredIds.length > 0 && filteredIds.every((id) => selected.has(id));

    const totalPages = Math.max(1, Math.ceil(filtered.length / SHORT_DUTY_PAGE_SIZE));
    const safePage = Math.min(page, totalPages);
    const pageStart = (safePage - 1) * SHORT_DUTY_PAGE_SIZE;
    const pagedRows = filtered.slice(pageStart, pageStart + SHORT_DUTY_PAGE_SIZE);

    useEffect(() => {
        setPage(1);
    }, [search, deptFilter, items.length]);

    // ---- per-row setters ----
    const patchRow = (id, patch) => setItems((prev) => prev.map((it) => (it.attendance_id === id ? { ...it, ...patch } : it)));
    const setMode = (id, mode) => patchRow(id, { mode });
    const setDeduction = (id, value) => {
        let v = value === "" ? "" : Number(value);
        if (v !== "" && v < 0) v = 0;
        if (v !== "" && v > 1) v = 1;
        patchRow(id, { deduction: v });
    };
    const setFine = (id, value) => {
        let v = value === "" ? "" : Number(value);
        if (v !== "" && v < 0) v = 0;
        patchRow(id, { fine: v });
    };

    // ---- selection ----
    const toggleRow = (id) => setSelected((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });
    const toggleAll = () => setSelected((prev) => {
        if (allFilteredSelected) {
            const next = new Set(prev);
            filteredIds.forEach((id) => next.delete(id));
            return next;
        }
        return new Set([...prev, ...filteredIds]);
    });

    // ---- bulk actions on the selected rows ----
    const applyModeToSelected = (mode) => {
        setBulkMode(mode);
        setItems((prev) => prev.map((it) => (selected.has(it.attendance_id) ? { ...it, mode } : it)));
    };
    const applyConfigToSelected = () =>
        setItems((prev) => prev.map((it) => (selected.has(it.attendance_id)
            ? { ...it, ...(it.mode === "fine" ? { fine: defaults.fine } : { deduction: defaults.fraction }) }
            : it)));

    // ---- totals ----
    const totals = useMemo(() => {
        const employees = new Set(items.map((it) => it.employee_id)).size;
        const deductDays = items.filter((it) => it.mode === "deduction").reduce((a, it) => a + (Number(it.deduction) || 0), 0);
        const fineTotal = items.filter((it) => it.mode === "fine").reduce((a, it) => a + (Number(it.fine) || 0), 0);
        return {
            days: items.length,
            employees,
            deductDays: Math.round(deductDays * 100) / 100,
            fineTotal: Math.round(fineTotal * 100) / 100,
        };
    }, [items]);

    const proceedToPayroll = async () => {
        if (!Array.isArray(payload?.department) || payload.department.length === 0) {
            ToastMsg("error", "Please select Department before running payroll");
            navigate("/payroll/payrollprocess", { replace: true });
            return;
        }
        if (!Array.isArray(payload?.employee_type) || payload.employee_type.length === 0) {
            ToastMsg("error", "Please select Employee Type before running payroll");
            navigate("/payroll/payrollprocess", { replace: true });
            return;
        }
        const res = await create(PAYROLL_RUN_GET, payload, false, setRunning);
        if (res?.status === "success") navigate("/payroll/runpayrollprocess", { replace: true, state: { PaySlipId: res.data } });
        else ToastMsg("error", res?.message || "Failed to start payroll process");
    };

    const handleFinalize = async () => {
        if (!editPermission) { ToastMsg("error", "You don't have permission to run payroll"); return; }
        setRunning(true);
        try {
            const periodCheck = await create(PAYROLL_RUN_GET, { ...payload, validate_only: true }, false);
            if (periodCheck?.status !== "success") {
                ToastMsg("error", periodCheck?.message || "Payroll has already been processed for this period");
                setRunning(false);
                return;
            }
            if (items.length) {
                const saveRes = await update(PAYROLL_SHORT_DUTY_REVIEW, {
                    from_date: payload.from_date,
                    to_date: payload.to_date,
                    items: items.map((it) => ({
                        attendance_id: it.attendance_id,
                        mode: it.mode,
                        deduction: Number(it.deduction) || 0,
                        fine: Number(it.fine) || 0,
                    })),
                }, false);
                if (saveRes?.status !== "success") {
                    ToastMsg("error", saveRes?.message || "Could not save short-duty decisions");
                    setRunning(false);
                    return;
                }
            }
            await proceedToPayroll();
        } catch (e) {
            ToastMsg("error", e?.message || "Something went wrong");
        } finally {
            setRunning(false);
        }
    };

    if (periodBlocked) {
        return null;
    }

    if (loading) {
        return (
            <div className="h-[calc(100vh-100px)] dark:bg-gray-900 flex flex-col items-center justify-center rounded-md">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-sidebar-primary mb-6"></div>
                <p className="text-md text-gray-700 dark:text-gray-300">Checking short-duty attendance...</p>
            </div>
        );
    }

    return (
        <ContainerCard>
            {/* Header */}
            <div className="flex items-center gap-4 mb-6">
                <ChevronLeftIcon
                    className="h-5 w-5 cursor-pointer text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                    onClick={() => navigate("/payroll/payrollprocess")}
                />
                <div className="flex-1">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Short-Duty Review</h1>
                    <p className="text-base text-gray-600 dark:text-gray-400 mt-1">
                        Choose a penalty for each missing-punch day before running payroll
                        {payload?.from_date && payload?.to_date && (
                            <> &middot; {moment(payload.from_date).format("DD MMM")} – {moment(payload.to_date).format("DD MMM YYYY")}</>
                        )}
                    </p>
                </div>
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <SummaryCard icon={Users} tint="blue" label="Employees affected" value={totals.employees} />
                <SummaryCard icon={Clock} tint="amber" label="Short-duty days" value={totals.days} />
                <SummaryCard icon={AlertTriangle} tint="rose" label="Deduction days (LOP)" value={totals.deductDays} />
                <SummaryCard icon={IndianRupee} tint="violet" label="Fine total" value={`₹${totals.fineTotal.toLocaleString("en-IN")}`} />
            </div>

            {/* Toolbar: search + department filter */}
            <div className="flex flex-wrap items-center gap-3 mb-3">
                <div className="relative flex-1 min-w-[220px] max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by name or employee code…"
                        className="pl-9"
                    />
                </div>
                <SelectField
                    size="sm"
                    value={deptFilter}
                    onChange={(e) => setDeptFilter(e.target.value)}
                    className="w-auto min-w-[12rem]"
                >
                    <option value="all">All departments</option>
                    {departments.map((d) => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                </SelectField>
                <span className="text-xs text-gray-400">
                    {filtered.length} of {items.length} total
                    {filtered.length > SHORT_DUTY_PAGE_SIZE && (
                        <> · page {safePage} of {totalPages}</>
                    )}
                </span>
            </div>

            {/* Bulk actions — operate on the selected rows */}
            {filtered.length > 0 && (
                <div className="flex flex-wrap items-center gap-3 mb-3">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                        {selected.size} selected
                    </span>
                    {/* common penalty switch */}
                    <div className="inline-flex items-center gap-2">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Set penalty:</span>
                        <div className={cn("inline-flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden", !selected.size && "opacity-40")}>
                            <ModeBtn active={bulkMode === "deduction"} disabled={!selected.size} onClick={() => applyModeToSelected("deduction")}>Deduct</ModeBtn>
                            <ModeBtn active={bulkMode === "fine"} disabled={!selected.size} onClick={() => applyModeToSelected("fine")}>Fine</ModeBtn>
                        </div>
                    </div>
                    {/* apply configured default value */}
                    <button
                        type="button"
                        disabled={!selected.size}
                        onClick={applyConfigToSelected}
                        className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-sidebar-primary text-white hover:brightness-90 transition disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        Apply configured value (deduct {defaults.fraction} day / fine ₹{Number(defaults.fine).toLocaleString("en-IN")})
                    </button>
                    {!selected.size && <span className="text-xs text-gray-400">Tick rows to enable</span>}
                </div>
            )}

            {/* Table */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden mb-3">
                <div className="max-h-[min(420px,calc(100vh-380px))] overflow-auto">
                    <table className="w-full text-sm">
                        <thead className="sticky top-0 z-10">
                            <tr className="bg-gray-50 dark:bg-gray-900 text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                <th className="px-4 py-3 w-10">
                                    <input type="checkbox" checked={allFilteredSelected} onChange={toggleAll} className="rounded border-gray-300 text-sidebar-primary focus:ring-sidebar-primary/30 cursor-pointer" />
                                </th>
                                <th className="px-4 py-3 font-medium">Employee</th>
                                <th className="px-4 py-3 font-medium">Department</th>
                                <th className="px-4 py-3 font-medium">Date</th>
                                <th className="px-4 py-3 font-medium">Check-in</th>
                                <th className="px-4 py-3 font-medium">Reason</th>
                                <th className="px-4 py-3 font-medium">Penalty</th>
                                <th className="px-4 py-3 font-medium text-right">Value</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="px-4 py-10 text-center text-gray-500 dark:text-gray-400">
                                        <ShieldCheck className="h-8 w-8 mx-auto mb-2 text-emerald-500" />
                                        {items.length === 0 ? "No short-duty days for this period." : "No rows match your search / filter."}
                                    </td>
                                </tr>
                            ) : (
                                pagedRows.map((it) => (
                                    <tr key={it.attendance_id} className={cn("hover:bg-gray-50/60 dark:hover:bg-gray-700/30", selected.has(it.attendance_id) && "bg-sidebar-primary/5")}>
                                        <td className="px-4 py-3">
                                            <input type="checkbox" checked={selected.has(it.attendance_id)} onChange={() => toggleRow(it.attendance_id)} className="rounded border-gray-300 text-sidebar-primary focus:ring-sidebar-primary/30 cursor-pointer" />
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="font-medium text-gray-900 dark:text-white">{it.employee_name}</div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400">{it.employee_code}</div>
                                        </td>
                                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{it.department_name || "-"}</td>
                                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{it.date ? moment(it.date).format("DD MMM YYYY") : "-"}</td>
                                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{it.check_in ? moment(it.check_in).format("hh:mm A") : "-"}</td>
                                        <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{it.description || "Missing punch"}</td>
                                        <td className="px-4 py-3">
                                            {/* Click to change this row directly; or tick rows and use the header "Set penalty" switch for bulk */}
                                            <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                                                <ModeBtn active={it.mode === "deduction"} onClick={() => setMode(it.attendance_id, "deduction")}>Deduct</ModeBtn>
                                                <ModeBtn active={it.mode === "fine"} onClick={() => setMode(it.attendance_id, "fine")}>Fine</ModeBtn>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center justify-end gap-2">
                                                {it.mode === "fine" ? (
                                                    <div className="relative w-28">
                                                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">₹</span>
                                                        <input
                                                            type="number" min="0" step="50" value={it.fine}
                                                            onChange={(e) => setFine(it.attendance_id, e.target.value)}
                                                            className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 pl-5 pr-2 py-1.5 text-sm text-right text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-sidebar-primary/25 focus:border-sidebar-primary"
                                                        />
                                                    </div>
                                                ) : (
                                                    <>
                                                        <input
                                                            type="number" min="0" max="1" step="0.5" value={it.deduction}
                                                            onChange={(e) => setDeduction(it.attendance_id, e.target.value)}
                                                            className="w-20 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2.5 py-1.5 text-sm text-right text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-sidebar-primary/25 focus:border-sidebar-primary"
                                                        />
                                                        <span className="text-xs text-gray-400 w-8">day</span>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {filtered.length > SHORT_DUTY_PAGE_SIZE && (
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4 text-sm text-gray-600 dark:text-gray-400">
                    <span>
                        Showing {pageStart + 1}–{Math.min(pageStart + SHORT_DUTY_PAGE_SIZE, filtered.length)} of {filtered.length}
                    </span>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            disabled={safePage <= 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-xs font-medium disabled:opacity-40"
                        >
                            Previous
                        </button>
                        <button
                            type="button"
                            disabled={safePage >= totalPages}
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-xs font-medium disabled:opacity-40"
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}

            {/* Footer actions — sticky so Finalize stays reachable */}
            <div className="sticky bottom-0 z-20 flex items-center justify-end gap-3 py-3 px-1 -mx-1 bg-[var(--background)]/95 backdrop-blur border-t border-gray-200 dark:border-gray-700">
                <button
                    type="button"
                    onClick={() => navigate("/payroll/payrollprocess")}
                    className="px-4 py-2.5 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    disabled={running}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={handleFinalize}
                    disabled={running || !editPermission}
                    className={cn(
                        "inline-flex items-center gap-2 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
                        PRIMARY_BUTTON_CLASS,
                    )}
                >
                    {running ? (
                        <span className="h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                    ) : (
                        <Play className="h-4 w-4" />
                    )}
                    Finalize & Run Payroll
                </button>
            </div>
        </ContainerCard>
    );
};

const TINTS = {
    blue: "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400",
    amber: "bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400",
    rose: "bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400",
    violet: "bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400",
};

const SummaryCard = ({ icon: Icon, tint, label, value }) => (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 flex items-center justify-between">
        <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">{label}</p>
            <p className="text-xl font-semibold text-gray-900 dark:text-white">{value}</p>
        </div>
        <div className={cn("w-11 h-11 rounded-xl flex items-center justify-center", TINTS[tint])}>
            <Icon className="h-5 w-5" />
        </div>
    </div>
);

const ModeBtn = ({ active, onClick, children, disabled }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={cn(
            "px-3 py-1.5 text-xs font-medium transition-colors disabled:cursor-not-allowed",
            active ? "bg-sidebar-primary text-white" : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
        )}
    >
        {children}
    </button>
);

export default ShortDutyReview;
